import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { SITE, ASSETS, PREMIUM_DISCLOSURE } from "@/config/site";

const COLS = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "/#features" },
      { label: "How It Works", href: "/#how-it-works" },
      { label: "Nova Premium", href: "/#premium" },
      { label: "Security & Privacy", href: "/#security" },
    ],
  },
  {
    title: "Help",
    links: [
      { label: "Support", href: "/support" },
      { label: "Contact", href: "/contact" },
      { label: "Restore Purchases", href: "/support#restore" },
      { label: "Delete Account", href: "/delete-account" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Use", href: "/terms" },
    ],
  },
];

const FooterLink = ({ href, label, testid }) =>
  href.startsWith("/#") || href.includes("#") ? (
    <a href={href} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={testid}>
      {label}
    </a>
  ) : (
    <Link to={href} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={testid}>
      {label}
    </Link>
  );

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="relative border-t border-white/10 bg-nova-bg" data-testid="site-footer">
      <div className="container-nova py-16">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={ASSETS.icon} alt="Nova app icon" className="h-10 w-10 rounded-xl" width="40" height="40" />
              <div>
                <p className="font-heading text-lg font-bold text-white">{SITE.name}</p>
                <p className="text-xs text-white/50">{SITE.tagline}</p>
              </div>
            </div>
            <a
              href={`mailto:${SITE.supportEmail}`}
              className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-nova-cyan"
              data-testid="footer-support-email"
            >
              <Mail size={16} /> {SITE.supportEmail}
            </a>
            <p className="mt-4 max-w-xs text-xs leading-relaxed text-white/40">
              Nova is a budgeting and financial-planning app. Not a bank, lender, credit agency or financial adviser.
            </p>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <p className="label-eyebrow mb-4 text-white/50">{col.title}</p>
              <ul className="flex flex-col gap-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <FooterLink
                      href={l.href}
                      label={l.label}
                      testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <p className="text-xs leading-relaxed text-white/45">{PREMIUM_DISCLOSURE}</p>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-white/40">
            © {year} {SITE.copyrightName}. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-nova-emerald" /> Coming soon to the App Store
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
